"use strict";
const EventEmitter = require("events").EventEmitter;
const _ = require("lodash");
const minimatch = require("minimatch");

/**
 * @typedef RegisteredDetails
 * @description An object that describes something that has been registered.
 * @type {Object}
 * @property {string} namespace - The dot notation namespace the object was registered under.
 * @property {string} name - The unique name the object was registered under.
 * @property {object} objToRegister - A pointer to the object that was registered.
 */
/**
 * @typedef RegistryCacheEntry
 * @description An entry in the registry cache. Where the Keys are the registered name and the
 * value is the registered object.
 * @type {Object.<string,RegisteredDetails>}
 */

/**
 * @typedef RegistryCache
 * @description An cache of registered objects, where the key is the registered namespace,
 * and the value is an Object thats key is the registered name and its value
 * is the registered object.
 * @type {Object.<string,RegistryCacheEntry>}
 * @type {Object}
 */
/**
 * @typedef NameDetails
 * @description An object containing the name and namespace details.
 * @type {Object}
 * @property {string} namespace - The dot notation namespace.
 * @property {string} name - The unique name.
 */
class Registry extends EventEmitter {
    constructor() {
        super();
        this._cache = {};
    }

    /**
     * Get the Fully Qualified name from name and namespace.
     * @param {string} namespace - The dot notation namespace.
     * @param {string} name - The unique name.
     * @returns {string} The fully qualified name.
     */
    getFullyQualifiedName(namespace, name) {
        return `${namespace}.${name}`;
    }

    /**
     * Get the name and namespace from a Fully Qualified name.
     * @param {string} fullyQualifiedName - The Fully Qualified name.
     * @param {string} [errMsg=''] - An optional error message to throw if fullyQualifiedName is NOT a Fully Qualified Name.
     * @returns {NameDetails} The details about the fully qualified name.
     * @throws {Error} If the provided arguments are invalid
     */
    getNameAndNamespace(namespaced, errMsg) {
        let firstDot = namespaced.lastIndexOf(".");
        if (firstDot !== -1) {
            return {
                namespace: namespaced.substr(0, firstDot),
                name: namespaced.substr(firstDot + 1),
            };
        } else {
            throw new Error(errMsg);
        }
    }

    /**
     * Register an object
     * @param {string} namespace - The dot notation namespace to register objToRegister under.
     * @param {string} name - The unique name to register objToRegister under.
     * @param {object} objToRegister - The object being registered.
     * @throws {Error} If the provided arguments are invalid
     */
    register(namespace, name, objToRegister) {
        if (arguments.length === 2) {
            objToRegister = arguments[1];
            let nameAndNamespace = this.getNameAndNamespace(
                arguments[0],
                "Can't register an object without a namespace."
            );
            name = nameAndNamespace.name;
            namespace = nameAndNamespace.namespace;
        }
        if (!_.isString(namespace)) {
            throw new Error("Can't register an object without a namespace.");
        }
        if (!name) {
            throw new Error("Can't register an object without a name.");
        }
        if (_.isEmpty(this._cache[namespace])) {
            this._cache[namespace] = {};
        }
        let registeredDetails = {
            registeredDetails: {
                namespace: namespace,
                name: name,
            },
            registeredObj: objToRegister,
        };
        //If we are stomping on a previously registered  object
        if (Object.hasOwn(this._cache[namespace], name)) {
            registeredDetails.replaced = this._cache[namespace][name];
            this.emit("RegisteredReplaced", registeredDetails);
        }
        this._cache[namespace][name] = {
            registeredDetails: {
                namespace: namespace,
                name: name,
            },
            registeredObj: objToRegister,
        };

        this.emit("Registered", registeredDetails);
    }

    /**
     * Unregisters an object
     * @param {string} namespace - The dot notation namespace to unregister from.
     * @param {string} name - The unique name of the object to unregister.
     * @param {boolean} silent - If true will not fire an unregistered event.
     * @returns {RegisteredDetails} The details about the object that was just unregistered.
     * @throws {Error} If the provided arguments are invalid
     */
    unregister(namespace, name, silent) {
        if (arguments.length === 1) {
            let nameAndNamespace = this.getNameAndNamespace(
                arguments[0],
                "Can't unregister an object without a namespace."
            );
            name = nameAndNamespace.name;
            namespace = nameAndNamespace.namespace;
        }
        if (!_.isString(namespace)) {
            throw new Error("Can't unregister an object without a namespace.");
        }
        if (!name) {
            throw new Error("Can't unregister an object without a name.");
        }

        let removed = this.getRegistered(namespace, name);

        if (removed) {
            _.unset(this._cache[namespace], name);
            if (_.isEmpty(this._cache[namespace])) {
                delete this._cache[namespace];
            }
            let removedDetails = {
                registeredDetails: {
                    namespace: namespace,
                    name: name,
                },
                registeredObj: removed,
            };
            if (!silent) {
                this.emit("UnRegistered", removedDetails);
            }
            return removedDetails;
        } else {
            return false;
        }
    }

    /**
     * Unregister all the objects currently in this cache.
     * @returns {RegistryCache} All the objects that were unregistered.
     */
    unregisterAll() {
        let details = {};
        Object.keys(this._cache).forEach((namespace) => {
            details[namespace] = {};
            Object.keys(this._cache[namespace]).forEach((name) => {
                let unregisteredDetails = this.unregister(namespace, name, true);
                details[namespace][name] = unregisteredDetails.registeredObj;
            });
        });
        this.emit("AllUnregistered", details);
        return details;
    }

    /**
     * Get the currently registered object by namespace and name.
     * @param {string|string[]} namespace - The dot notation namespace of the object to retrieve, or an array of namespaces in order of preference.
     * @param {string} name - The unique name of the object to retrieve.
     * @returns {*} The object that is currently registered to that namespace and name or null if NOT found.
     * @throws {Error} If the provided arguments are invalid
     */
    getRegistered(namespace, name) {
        let found;
        if (arguments.length === 1) {
            let nameAndNamespace = this.getNameAndNamespace(
                arguments[0],
                "Can't get a registered object without a namespace."
            );
            name = nameAndNamespace.name;
            namespace = nameAndNamespace.namespace;
        }
        if (!name) {
            throw new Error("Can't get a registered object without a name.");
        }
        if (!_.isString(namespace)) {
            if (_.isArray(namespace)) {
                namespace.some((ns) => {
                    found = this.getRegistered(ns, name);
                    if (found) {
                        return true;
                    }
                });
            } else {
                throw new Error(
                    "Can't get a registered object without a namespace or array of namespaces."
                );
            }
        } else {
            found = _.get(this._cache[namespace], name, null);
        }
        return found;
    }

    /**
     * Get all the registered objects.
     * This loops through the cache and
     * return pointers to all the registered objects in the same format as the cache.
     * @returns {RegistryCache} All the currently registered object.
     */
    getAllRegistered() {
        let allRegistered = {};
        Object.keys(this._cache).forEach((namespace) => {
            allRegistered[namespace] = {};
            Object.keys(this._cache[namespace]).forEach((name) => {
                allRegistered[namespace][name] = this._cache[namespace][name];
            });
        });
        return allRegistered;
    }

    /**
     * Get all the registered objects.
     * This loops through the cache and
     * return pointers to all the registered objects in a flattened format.
     * @returns {Array.<RegisteredDetails>} All the currently registered object.
     */
    getAllRegisteredFlat() {
        let allRegistered = [];
        Object.keys(this._cache).forEach((namespace) => {
            Object.keys(this._cache[namespace]).forEach((name) => {
                allRegistered.push(this._cache[namespace][name]);
            });
        });
        return allRegistered;
    }

    /**
     * Get all the registered objects via namespace using glob, or an array of globs
     * @param {string|string[]} [namespaceGlob]
     * @returns {RegistryCache} All the currently registered objects that match the glob.
     * @throws {Error} If the provided arguments are invalid
     */

    getAllRegisteredByGlob(glob) {
        if (!_.isString(glob) && !_.isArray(glob)) {
            throw new Error("Can't get all registered objects without a namespace or glob.");
        }
        let matched = {};
        const globs = _.isArray(glob) ? glob : [glob];
        const seen = new Set();

        // For each glob, in order
        globs.forEach((oneGlob) => {
            oneGlob = oneGlob.replace(/\./g, "/");
            Object.keys(this._cache).forEach((namespace) => {
                Object.keys(this._cache[namespace]).forEach((name) => {
                    let dotFQN = this.getFullyQualifiedName(namespace, name);
                    let pathName = dotFQN.replace(/\./g, "/");
                    let uniqueKey = namespace + "::" + name;
                    if (!seen.has(uniqueKey) && minimatch(pathName, oneGlob)) {
                        if (!Object.hasOwn(matched, namespace)) {
                            matched[namespace] = {};
                        }
                        matched[namespace][name] = this._cache[namespace][name];
                        seen.add(uniqueKey);
                    }
                });
            });
        });

        return matched;
    }

    /**
     * Checks to see if an object has been previosuly registered using strict equality (===).
     * @param {*} registeredObj - The object to test if its registered.
     * @returns {RegisteredDetails} The registration details if registered false otherwise.
     */
    isObjectRegistered(registeredObj) {
        let results = false;
        Object.keys(this._cache).some((namespace) => {
            let found = Object.keys(this._cache[namespace]).some((name) => {
                if (this._cache[namespace][name].registeredObj === registeredObj) {
                    results = this._cache[namespace][name];
                    return true;
                }
            });
            if (found) {
                return true;
            }
        });
        return results;
    }

    /**
     * Checks to see if anything is registered to a specific namespace and name.
     * @param {string} namespace - The dot notation namespace of the object to retrieve.
     * @param {string} name - The unique name of the object to retrieve.
     * @returns {boolean} True if registered false otherwise.
     * @throws {Error} If the provided arguments are invalid
     */
    isRegistered(namespace, name) {
        if (arguments.length === 1) {
            let nameAndNamespace = this.getNameAndNamespace(
                arguments[0],
                "Can't check to see if an object is without a namespace."
            );
            name = nameAndNamespace.name;
            namespace = nameAndNamespace.namespace;
        }
        if (!_.isString(namespace)) {
            throw new Error("Can't check to see if an object is without a namespace.");
        }
        if (!name) {
            throw new Error("Can't check if an object is registered without a name.");
        }
        let registered = this.getRegistered(namespace, name);
        return !!registered;
    }

    /**
     * Checks to see if the cache is empty.
     * @returns {boolean} True if the cache is empty False otherwise.
     */
    isEmpty() {
        return _.isEmpty(this._cache);
    }

    /**
     * Retrieves pointers to all the registered objects in the cache in the same format as the cache,
     * HOWEVER this is NOT a pointer to the cache object. This is done to prevent external manipulation of the cache.
     * @returns {RegistryCache}
     */
    get cache() {
        return this.getCache();
    }

    /**
     * Retrieves pointers to all the registered objects in the cache in the same format as the cache,
     * HOWEVER this is NOT a pointer to the cache object. This is done to prevent external manipulation of the cache.
     * @returns {RegistryCache}
     */
    getCache() {
        return this.getAllRegistered();
    }

    /**
     * Will crawl through all the registered objects and if any of them have a shutdown method that will be called.
     * @returns {Promise}
     */
    shutdown() {
        let promises = [];
        let all = this.getAllRegisteredFlat();
        all.forEach((registered) => {
            if (_.isFunction(registered.registeredObj.shutdown)) {
                promises.push(registered.registeredObj.shutdown());
            } else if (_.isFunction(registered.registeredObj.shutDown)) {
                promises.push(registered.registeredObj.shutDown());
            }
        });
        let allProms = Promise.all(promises);
        allProms.then(() => {
            this._cache = undefined;
        });
        return allProms;
    }
}

module.exports = Registry;
